<script setup>
import {onMounted, reactive, ref, computed, watch, nextTick } from "vue";
import axios from "axios";
import { dateUtil } from '@functions/dateUtil.js';
import EnifClubApplyModal from '@templates/setting/modal/EnifClubApplyModal.vue';
import { url } from '@/config/urls';
import { useHeaderStore } from "@stores/header.js";
import router from '@/router/index.js';
import { useRoute } from "vue-router";
import { modalUtil } from '@functions/modalUtil.js';
import BarcodeScanApplyModal from '@templates/setting/modal/BarcodeScanApplyModal.vue';
import DeleteBarcodeScanDeviceConfirmModal from "@parts/modal/DeleteBarcodeScanDeviceConfirmModal.vue";
import DeleteBarcodeScanDeviceCompleteModal from "@parts/modal/DeleteBarcodeScanDeviceCompleteModal.vue";
import deviceService from '@/services/deviceService.js';
import { Capacitor } from '@capacitor/core';
import { useServiceApplyStore } from '@stores/serviceApply';
import ApiErrorModal from '@parts/ApiErrorModal.vue';
import { apiError } from '@functions/apiError.js';


// コンポーネントに渡されたpropsの取得
const props = defineProps({
  isAuthenticated: Boolean,
  loginUrl: {type: String, default: url.login},
  userDataUrl: {type: String, default: url.api.userData},
  emailUrl: {type: String, default: url.api.email},
  emailSettingUrl: {type: String, default: url.api.emailSetting},
  verificationCodeVerifyUrl: {type: String, default: url.api.verificationCode.verify},
  verificationCodeResendUrl: {type: String, default: url.api.verificationCode.resend},
  settingPasswordPageUrl: {type: String, default: url.page.setting.password},
  errorPageUrl: {type: String, default: url.error},
  layoutSettingUrl: {type: String, default: url.api.layoutSetting},
  serviceApplyGetApiUrl: {type: String, default: url.api.serviceApply.get},
  applyEnifClubApiUrl: {type: String, default: url.api.serviceApply.applyEnifClub},
  messageApiUrl: {type: String, default: url.api.messages},
  applyBarcodeScanApiUrl: {type: String, default: url.api.serviceApply.applyBarcodeScan},
  mobileDeviceDeleteApiUrl: {type: String, default: url.api.mobileDevices.delete},
  mobileDeviceListApiUrl: {type: String, default: url.api.mobileDevices.list}
});

const { formatDateWithSlash } = dateUtil();
const { formatDateWithHourMin } = dateUtil();
const { showModal } = modalUtil();
const headerStore = useHeaderStore();
const route = useRoute();

const email = ref('');
const emailStatus = ref(false);
const shortageMailFlg = ref(false);
const beforeUpdateShortageMailFlg = ref(false);
const isEmailEditing = ref(false);
const verificationCode = ref('');
const passwordLastUpdated = ref('2000年1月1日');
const shouldPasswordChange = ref(false);
const errorMessage = reactive({
  email: '',
  verificationCode: '',
  shortageMailFlg: '',
  orderListItemLayout: ''
});
const flushMessage = ref(sessionStorage.flushMessage);
const isFlushMessageShow = ref(false);
const isShowResendVerificationCode = ref(true);
const orderListItemLayout = ref(0);
const beforeUpdateOrderListItemLayout = ref(0);
const enifClubApplyExplanation = ref('');
const barcodeApplyExplanation = ref('');
const barcodeScanUseDeviceExplanation = ref('');
const totalNum = ref(0);
const barcodeScanDeviceList = ref([]);
const currentDeviceUuid = ref('');
// ローディングフラグ
const isLoading = ref(false);

// ローディングフラグ更新
const handleUpdateLoading = (loading) => {
  isLoading.value = loading;
}

const selectedDevice = ref(null);
// エラーメッセージの状態管理
const errorMessages = ref([]);

const user = reactive({
  medicalSuppliesSplit: null,
  medicalMaterialsSplit: null,
  orderFlg: null,
  isPermittedOrder: null,
  returnRequestFlg: null,
  enifClubApplyPermissionFlg: null,
  enifClubJoinDate: null,
  deliveryDateAllowedFlg: null,
  splitSiteCode: null,
  returnRequestFlg: null,
  isPermittedReturnRequest: null,
  barcodeScanApplyPermissionFlg: null,
});

const serviceApplyStore = useServiceApplyStore();
const enifClub = computed(() => serviceApplyStore.enifClub);
const barcodeScan = computed(() => serviceApplyStore.barcodeScan);

// メールアドレス編集・更新ボタン押下時
const onClickEmailUpdateButton = () => {
  if (!isEmailEditing.value) {
    isEmailEditing.value = true;
    return;
  }

  if (!email.value) {
    errorMessage.email = 'メールアドレスを入力してください。';
    return;
  } else if (email.value.length > 255) {
    errorMessage.email = 'メールアドレスは255文字以内で入力してください。';
    return;
  } else if (!email.value.match(
      /^[a-zA-Z0-9_.+-]+@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/)) {
    errorMessage.email = '有効なメールアドレスを入力してください。';
    return;
  }

  // メールアドレス更新APIの呼び出し
  axios.post(props.emailUrl, {newEmail: email.value})
  .then(() => {
    if (headerStore) {
      headerStore.updateEmail(email.value);
    }
    
    verificationCode.value = '';
    emailStatus.value = false;
    errorMessage.email = '';
    isEmailEditing.value = false;

    flushMessage.value = 'メールアドレスを更新しました。';
    isFlushMessageShow.value = true;
    setTimeout(() => isFlushMessageShow.value = false, 3000);
  })
  .catch(error => {
    switch (error?.response?.status) {
      case 400:
        if (error.response.data.error instanceof Array) {
          errorMessage.email = error.response.data.error.map(
              errorObject => errorObject.field === 'newEmail' ? errorObject.message : null)
          .filter(item => item !== null)
          .join('\n');
        } else {
          sessionStorage.setItem('errorHeading', '指定されたページを表示できません。');
          sessionStorage.setItem('errorBody', '申し訳ございませんが、リクエストを正しく処理できませんでした。送信内容が正確であることを確認し、もう一度お試しください。');
          router.replace(props.errorPageUrl);
        }
        break;
      case 401:
        router.replace(props.loginUrl);
        break;
      default:
        router.replace(props.errorPageUrl);
        break;
    }
  });
};

// 送信ボタン押下時
const onClickSendButton = (event) => {
  event.target.disabled = true;
  if (verificationCode.value === '') {
    errorMessage.verificationCode = '確認コードを入力してください。';
    event.target.disabled = false;
    return;
  }

  // 確認コード検証APIの呼び出し
  axios.post(props.verificationCodeVerifyUrl, {verificationCode: verificationCode.value})
  .then(response => {
    emailStatus.value = response.data.response.verified;
    headerStore.updateVerified(emailStatus.value);
    errorMessage.verificationCode = emailStatus.value ? '' :
          response.data.error.map(errorObject => errorObject.message).join('\n');
    event.target.disabled = false;
  })
  .catch((error) => apiError(error, errorMessages));
};

// 確認コード再送ボタン押下時
const onClickVerificationCodeResendButton = (event) => {
  event.preventDefault();
  isShowResendVerificationCode.value = false;

  // 確認コード再送APIの呼び出し
  axios.get(props.verificationCodeResendUrl)
  .then(response => {
    if (response.data.response.resent) {
      flushMessage.value = '確認コードを再送しました。';
      isFlushMessageShow.value = true;
      setTimeout(() => {
        isShowResendVerificationCode.value = true;
        isFlushMessageShow.value = false;
      }, 3000);
    } else {
      errorMessage.verificationCode =
          response.data.error.map(errorObject => errorObject.message).join('\n');
      isShowResendVerificationCode.value = true;
    }
  })
  .catch((error) => apiError(error, errorMessages));
};

// 欠品時メール配信「送信」ボタン押下時
const onClickShortageMailFlgUpdateButton = () => {

  if(typeof shortageMailFlg.value !== 'boolean') {
    errorMessage.shortageMailFlg = '欠品時メール配信を選択してください。';
    return;
  }

  // 欠品時メール配信設定更新APIの呼び出し
  axios.post(props.emailSettingUrl, {shortageMailFlg: shortageMailFlg.value})
    .then(() => {
      errorMessage.shortageMailFlg = '';
      beforeUpdateShortageMailFlg.value = shortageMailFlg.value;

      flushMessage.value = '欠品時メール配信設定を更新しました。';
      isFlushMessageShow.value = true;
      setTimeout(() => isFlushMessageShow.value = false, 3000);
    })
    .catch(error => {
      switch (error.response.status) {
        case 400:
          sessionStorage.setItem('errorHeading', '指定されたページを表示できません。');
          sessionStorage.setItem('errorBody', '申し訳ございませんが、リクエストを正しく処理できませんでした。送信内容が正確であることを確認し、もう一度お試しください。');
          router.replace(props.errorPageUrl);
          break;
        case 401:
          router.replace(props.loginUrl);
          break;
        default:
          router.replace(props.errorPageUrl);
          break;
      }
    })
}

// 注文結果明細表示「送信」ボタン押下時
const onClickOrderListItemLayoutUpdateButton = () => {
  if (orderListItemLayout.value !== 0 && orderListItemLayout.value !== 1) {
    errorMessage.orderListItemLayout = '注文結果明細を選択してください。';
    return;
  }

  // 注文結果明細設定更新APIの呼び出し
  axios.post(props.layoutSettingUrl, {orderListItemLayout: orderListItemLayout.value})
    .then(() => {
      errorMessage.orderListItemLayout = '';
      beforeUpdateOrderListItemLayout.value = orderListItemLayout.value;

      flushMessage.value = '注文結果明細設定を更新しました。';
      isFlushMessageShow.value = true;
      setTimeout(() => isFlushMessageShow.value = false, 3000);
    })
    .catch(error => {
      switch (error?.response?.status) {
        case 400:
          sessionStorage.setItem('errorHeading', '指定されたページを表示できません。');
          sessionStorage.setItem('errorBody', '申し訳ございませんが、リクエストを正しく処理できませんでした。送信内容が正確であることを確認し、もう一度お試しください。');
          router.replace(props.errorPageUrl);
          break;
        case 401:
          router.replace(props.loginUrl);
          break;
        default:
          router.replace(props.errorPageUrl);
          break;
      }
    })
}
// パスワード変更ボタン押下時
const onClickPasswordChangeButton = () => router.push(props.settingPasswordPageUrl);

// 各種サービス申し込み状態の取得
const getServiceApply = async () => {
  try {
    await serviceApplyStore.fetchServiceApply(props.serviceApplyGetApiUrl);
  } catch(error) {
    apiError(error, errorMessages);
  }
}

// ENIFクラブ申し込み処理
const applyEnifClub = async() => {
  try {
    await axios.post(props.applyEnifClubApiUrl);

    await getServiceApply();
    await getBarcodeScanDevices();
    
  } catch(error) {
    apiError(error, errorMessages);
  }
}

// バーコードスキャン機能申し込み処理
const applyBarcodeScan = async() => {
  try {
    await axios.post(props.applyBarcodeScanApiUrl);
    
    await getServiceApply();
    await getBarcodeScanDevices(); 
  } catch(error) {
    apiError(error, errorMessages);
  }
}

// バーコードスキャン端末一覧取得処理
const getBarcodeScanDevices = async() => {
  handleUpdateLoading(true);
  try {
    const response = await axios.get(props.mobileDeviceListApiUrl, {
      params: { onlyBarcodeScanDevices: true }
    });
    barcodeScanDeviceList.value = response.data.response.mobileDeviceList ?? [];
    totalNum.value = parseInt(response.data.response.totalNum);
  } catch(error) {
    apiError(error, errorMessages);
  } finally {
    handleUpdateLoading(false);
  }
}

// バーコードスキャン削除確認モーダル表示
const openDeleteModal = async(device) => {
  selectedDevice.value = device;
  // デバイス削除確認モーダルを表示する
  showModal('deleteBarcodeScanDeviceConfirmModal');
  
}

// バーコードスキャン削除処理
const confirmDeleteDevice = async() => {
  if(!selectedDevice.value) return;

  try {
    await axios.post(props.mobileDeviceDeleteApiUrl, {
      uuid: selectedDevice.value.uuid });
    await getBarcodeScanDevices();

    const modalElement = document.getElementById('deleteBarcodeScanDeviceConfirmModal');
    const modalInstance = bootstrap.Modal.getInstance(modalElement);
    modalInstance.hide();

    showModal('deleteBarcodeScanDeviceCompleteModal')
    
  } catch(error) {
    apiError(error, errorMessages);
  } finally {
    selectedDevice.value = null;
  }
}

// 利用申し込み説明文の取得
const getApplyExplanation = () => {
  axios.get(props.messageApiUrl, { params: {screenId: 'frontend_setting'} })
  .then(response => {
    const messages = response.data.response.messages;
    enifClubApplyExplanation.value = messages.enif_club_apply_explanation ?? '';
    barcodeApplyExplanation.value = messages.barcode_scan_apply_explanation ?? '';
    barcodeScanUseDeviceExplanation.value = messages.barcode_scan_use_device_explanation ?? '';
  })
  .catch((error) => apiError(error, errorMessages));
}

const enifClubApplyStatus = computed(() => {
  const applied = enifClub.value?.applied ?? 0;
  if (user.medicalSuppliesSplit === 0  && user.medicalMaterialsSplit === 0
        && (applied === 0 || applied === 3) && user.enifClubApplyPermissionFlg) {
    return '未申込';
  } else if (enifClub.value?.applied === 1 && user.enifClubJoinDate === '') {
    return '申込済';
  } else if (user.enifClubJoinDate) {
    return '利用開始済';
  } else {
    return '申込不可';
  }
});

// バーコードスキャン機能解約から60日経過している場合はTRUE(申し込み許可)を返し、それ以外はfalse
const isBarcodeScanApplyAllowed = computed(() => {
  if(barcodeScan.value?.applied !== 3 || !barcodeScan.value?.canceledDate) {
    return false;
  }

  const cancelledDateStr = barcodeScan.value?.canceledDate;

  const parts = cancelledDateStr.includes('/')
    ? cancelledDateStr.split('/')
    : [cancelledDateStr.slice(0, 4), cancelledDateStr.slice(4, 6), cancelledDateStr.slice(6, 8)];
  const cancelledDate = new Date(
    Number(parts[0])
    , Number(parts[1]) -1
    , Number(parts[2])
  );
  // 本日日付を0時00分で取得する
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffDays = (today - cancelledDate) / (1000 * 60 * 60 * 24);

  // 解約日から60日以上経過している場合 true;
  return diffDays >= 60;
})

// 現在のデバイス情報
const isCurrentDevice = (uuid) => {
  return uuid && uuid === currentDeviceUuid.value;
};

// ハッシュ付き遷移時、対象セクション（個人設定/パスワード設定/機能追加の申し込み）へスクロール
const scrollToHashTarget = async () => {
  const hash = route.hash;
  if (!hash) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  await nextTick();
  let attempts = 0;
  const trySelectAndScroll = () => {
    const el = document.querySelector(hash);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (attempts < 2) {
      attempts++;
      setTimeout(trySelectAndScroll, 100);
    }
  };
  trySelectAndScroll();
};

watch(
  [() => route.hash, () => user.isPermittedOrder, () => user.isPermittedReturnRequest],
  scrollToHashTarget,
  { immediate: true }
);

// コンポーネントマウント後の処理
onMounted(async() => {
  // フラッシュメッセージの表示
  if (flushMessage.value !== undefined) {
    isFlushMessageShow.value = true;
    setTimeout(() => isFlushMessageShow.value = false, 3000);
    sessionStorage.removeItem('flushMessage');
  }

  try {
    // ユーザー情報取得APIの呼び出し
    const response = await axios.get(props.userDataUrl);

    email.value = response.data.response.email;
    emailStatus.value = response.data.response.emailStatus;
    shortageMailFlg.value = beforeUpdateShortageMailFlg.value = response.data.response.shortageMailFlg;
    orderListItemLayout.value = beforeUpdateOrderListItemLayout.value = Number(response.data.response.orderListItemLayout);

    user.medicalSuppliesSplit = response.data.response.medicalSuppliesSplit;
    user.medicalMaterialsSplit = response.data.response.medicalMaterialsSplit;
    user.orderFlg = response.data.response.orderFlg;
    user.isPermittedOrder = response.data.response.isPermittedOrder;
    user.enifClubApplyPermissionFlg = response.data.response.enifClubApplyPermissionFlg;
    user.enifClubJoinDate = formatDateWithSlash(response.data.response.enifClubJoinDate);
    user.deliveryDateAllowedFlg = response.data.response.deliveryDateAllowedFlg;
    user.splitSiteCode = response.data.response.splitSiteCode;
    user.returnRequestFlg = response.data.response.returnRequestFlg;
    user.isPermittedReturnRequest = response.data.response.isPermittedReturnRequest;
    user.barcodeScanApplyPermissionFlg = response.data.response.barcodeScanApplyPermissionFlg;

    const passwordUpdateDate = new Date(response.data.response.passwordUpdateDate);
    passwordLastUpdated.value = passwordUpdateDate.toLocaleDateString(
        'ja', {year: 'numeric', month: 'long', day: 'numeric'});

    const now = new Date();
    shouldPasswordChange.value = passwordUpdateDate <= new Date(
        now.getFullYear(), now.getMonth() - 3, now.getDate());
    headerStore.updateEmail(email.value);

    currentDeviceUuid.value = await deviceService.getDeviceUUID();
  } catch(error) {
    apiError(error, errorMessages);
  }

  // 各種サービス申し込み状態の取得
  getServiceApply();
  
  // ENIFクラブ利用申し込み説明文の取得
  getApplyExplanation();

  // バーコードスキャン端末一覧取得
  getBarcodeScanDevices();

});

// PDF遷移
const openPdf = (filename) => {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios' || platform === 'android') {
    router.push({
      path: '/pdf-viewer/enif-club-document'
      , query: {
        filename
        , splitSiteCode: user.splitSiteCode
        , returnPath: router.currentRoute.value.path
      }
    });
  } else {
    window.open(`/static/base_${user.splitSiteCode}/${filename}`, '_blank', 'noopener');
  }
};
</script>

<template>
    <div id="com_top_message1" :class="{ show: isFlushMessageShow }">{{ flushMessage }}</div>
    <div id="contents1">
      <div class="com_inner2 clearfix">
        <h2 class="com_title1">ユーザー管理</h2>
        <h3 id="personal-setting" class="com_title3">個人設定</h3>
        <div class="com_form_g1">
          <div><label>メールアドレス</label></div>
          <div>
            <div class="flx1">
              <input type="email" v-model="email" :readonly="!isEmailEditing"
                     class="form-control com_form1">
              <div v-if="emailStatus" class="com_validate_msg1 error ok">
                <img src="/front/assets/common/img/icon_mail_ok.png"
                     alt="確認済み" class="com_icon_mail_ok">
                確認済み
              </div>
              <button type="button" class="btn com_btn6 cl1"
                      @click="onClickEmailUpdateButton">{{ isEmailEditing ? '更新' : '編集' }}
              </button>
            </div>
            <div v-if="errorMessage.email" class="com_validate_msg1 error">
              {{ errorMessage.email }}
            </div>
            <div v-if="!emailStatus" class="com_validate_msg1 error">このメールアドレスは未確認です。<br>確認コードを入力してアクティベートしてください。</div>
          </div>
        </div>
        <div v-if="!emailStatus" class="com_form_g1">
          <div><label>確認コード</label></div>
          <div>
            <div class="flx2">
              <input type="text" v-model="verificationCode" class="form-control com_form1 wd2">
              <button type="button" class="btn com_btn6 cl1"
                      @click="onClickSendButton">送信
              </button>
            </div>
            <div v-if="errorMessage.verificationCode" class="com_validate_msg1 error text-pre-wrap">
              {{ errorMessage.verificationCode }}
            </div>
            <div class="com_validate_msg1 error resend" v-if="isShowResendVerificationCode">
              <a href="" @click.prevent="onClickVerificationCodeResendButton">確認コードを再送する</a>
            </div>
          </div>
      </div>
        <div class="com_form_g1">
          <div>
            <label>欠品時メール配信</label>
          </div>
          <div>
            <div class="flx2">
              <div>
                <div class="com_form_radio2">
                  <input class="com_form_radio1" type="radio" name="radioGroup" id="inlineRadio1"
                         :value="true"
                         v-model="shortageMailFlg"
                         :disabled="!emailStatus"
                  >
                  <label class="com_form_check_label1" for="inlineRadio1">受け取る</label>
                </div>
                <div class="com_form_radio2">
                  <input class="com_form_radio1" type="radio" name="radioGroup" id="inlineRadio2"
                         :value="false"
                         v-model="shortageMailFlg"
                         :disabled="!emailStatus"
                  >
                  <label class="com_form_check_label1" for="inlineRadio2">受け取らない</label>
                </div>
              </div>
              <button type="button" class="btn com_btn6 cl1"
                      :disabled="!emailStatus || beforeUpdateShortageMailFlg === shortageMailFlg"
                      @click="onClickShortageMailFlgUpdateButton">送信</button>
            </div>
            <div v-if="errorMessage.shortageMailFlg" class="com_validate_msg1 error">
              {{ errorMessage.shortageMailFlg }}
            </div>
          </div>
        </div>
        <div class="com_form_g1">
          <div>
            <label>注文結果明細表示</label>
          </div>
          <div>
            <div class="flx2">
              <div>
                <div class="com_form_radio2">
                  <input class="com_form_radio1" type="radio" name="layoutRadioGroup" id="designRadio"
                    :value="0"
                    v-model="orderListItemLayout"
                  >
                  <label class="com_form_check_label1" for="designRadio">デザイン版</label>
                </div>
                <div class="com_form_radio2">
                  <input class="com_form_radio1" type="radio" name="layoutRadioGroup" id="simpleRadio"
                    :value="1"
                    v-model="orderListItemLayout"
                  >
                  <label class="com_form_check_label1" for="simpleRadio">シンプル版</label>
                </div>
              </div>
              <button type="button" class="btn com_btn6 cl1"
                      :disabled="beforeUpdateOrderListItemLayout === orderListItemLayout"
                      @click="onClickOrderListItemLayoutUpdateButton">送信</button>
            </div>
            <div v-if="errorMessage.orderListItemLayout" class="com_validate_msg1 error">
              {{ errorMessage.orderListItemLayout }}
            </div>
          </div>
        </div>
        <hr class="com_sep1">
        <h3 id="password-setting" class="com_title3">パスワード設定</h3>
        <div class="com_fb_cnt1">
          <div>
            <p class="com_fs18 com_fs16s">パスワード最終更新日</p>
            <p class="com_fs18 com_fs16s">{{ passwordLastUpdated }}</p>
          </div>
          <div>
            <button type="button" class="btn com_btn6 b cl1"
                    @click="onClickPasswordChangeButton">パスワードを変更する
            </button>
          </div>
        </div>
        <div v-if="shouldPasswordChange" class="com_validate_msg1 error">
          お客様のパスワードは3か月以上更新されていません。
        </div>
        <template v-if="user.isPermittedOrder || user.isPermittedReturnRequest">
          <hr class="com_sep1">
          <div class="com_g_bg1">
            <h3 id="service-apply" class="com_title3">機能追加の申し込み</h3>
            <div class="com_cnt1" v-if="enifClubApplyStatus === '未申込' && user.isPermittedOrder">
              <div>
                <div class="hd1">
                  <h3>ENIFclub利用</h3>
                  <p class="com_fs14 com_mgb5">ENIFclubにお申し込みいただくと、医薬品分割のサービスがご利用いただけるようになります。</p>
                  <p class="com_fs14 com_color3 com_mgb0">{{ enifClubApplyExplanation }}</p>
                </div>
              </div>
              <div>
                <button type="button" class="btn com_btn6 b cl1" data-bs-toggle="modal" data-bs-target="#enifClubApplyModal">申し込み</button>
              </div>
            </div>
            <div class="com_cnt2" v-else-if="enifClubApplyStatus === '申込済' && user.isPermittedOrder">
              <div>
                <div class="hd1">
                  <h3>ENIFclub利用</h3>
                  <div class="hd1_dates1">
                    <span>申し込み日：{{ enifClub?.appliedDate || '-' }}</span>
                    <span>利用開始日：{{ user.enifClubJoinDate || '-' }}</span>
                  </div>
                </div>
                <p>申し込みが完了しました。以下の書類を記入し、担当MSにご連絡ください。</p>
                <ul class="link_list1" v-if="user.splitSiteCode !== null">
                  <li>
                    <a href="#" class="com_link2" @click.prevent="openPdf('enif_club_apply.pdf')">利用申込書
                      <img src="https://kyosomirai-portal.com/front/assets/common/img/icon_pdf1.png" alt="" class="com_icon_pdf1">
                    </a>
                  </li>
                  <li>
                    <a href="#" class="com_link2" @click.prevent="openPdf('enif_club_bank.pdf')">預金口座振替依頼書
                      <img src="https://kyosomirai-portal.com/front/assets/common/img/icon_pdf1.png" alt="" class="com_icon_pdf1">
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div class="com_cnt2" v-else-if="enifClubApplyStatus === '利用開始済' && user.isPermittedOrder">
              <div class="hd1">
                <h3>ENIFclub利用</h3>
                <div class="hd1_dates1">
                  <span>申し込み日：{{ enifClub?.appliedDate || '-' }}</span>
                  <span>利用開始日：{{ user.enifClubJoinDate || '-' }}</span>
                </div>
              </div>
              <p>現在ご利用中です。</p>
            </div>
            <div class="com_cnt1" v-else-if="enifClubApplyStatus === '申込不可' && user.isPermittedOrder">
              <div class="hd1">
                <h3>ENIFclub利用</h3>
              </div>
              <div>
                <button type="button" class="btn com_btnDisabled b" disabled>申し込み</button>
              </div>
            </div>
            <!-- 申込済かつ未解約：利用中 -->
            <div class="com_cnt2" v-if="(user.orderFlg === 0 || user.returnRequestFlg === 0) && user.barcodeScanApplyPermissionFlg === 1 && barcodeScan?.applied === 2">
              <div class="hd1">
                <h3>バーコードスキャン機能</h3>
                <span>利用開始日：{{ barcodeScan?.appliedDate ? formatDateWithSlash(barcodeScan.appliedDate) : '-' }}</span>
              </div>
              <p>現在ご利用中です。<br>スマートフォンアプリをご確認ください。</p>
            </div>
            <!-- 解約済かつ60日以上経過：再申込可 -->
            <div class="com_cnt1" v-else-if="(user.orderFlg === 0 || user.returnRequestFlg === 0) && user.barcodeScanApplyPermissionFlg === 1 && barcodeScan?.applied === 3 && isBarcodeScanApplyAllowed">
              <div>
                <div class="hd1">
                  <h3>バーコードスキャン機能</h3>
                  <p class="com_fs14 com_mgb5">バーコードスキャン機能にお申し込みいただくと、スマートフォンアプリにてバーコードスキャンがご利用いただけるようになります。</p>
                  <p class="com_fs14 com_color3 com_mgb0">{{ barcodeApplyExplanation }}</p>
                </div>
              </div>
              <div>
                <button type="button" class="btn com_btn6 b cl1" data-bs-toggle="modal" data-bs-target="#barcodeScanApplyModal">申し込み</button>
              </div>
            </div>
            <!-- 解約済みかつ60日未満：再申込不可 -->
            <div class="com_cnt2" v-else-if="(user.orderFlg === 0 || user.returnRequestFlg === 0) && user.barcodeScanApplyPermissionFlg === 1 && barcodeScan?.applied === 3 && !isBarcodeScanApplyAllowed">
              <div class="hd1">
                <h3>バーコードスキャン機能</h3>
                <span>解約日：{{ barcodeScan?.canceledDate ? formatDateWithSlash(barcodeScan.canceledDate) : '-' }}</span>
              </div>
              <p class="com_fs14 com_color3 com_mgb0" v-if="barcodeScan?.isAvailable">※解約月の月末までは引き続きスキャン機能をご利用いただけます</p>
              <p>
                現在は再申し込みできません。<br>
                解約後60日経過すると再度お申し込みいただけます。
              </p>
            </div>
            <!-- 未申込:新規申込可 -->
            <div class="com_cnt1" v-else-if="(user.orderFlg === 0 || user.returnRequestFlg === 0) && user.barcodeScanApplyPermissionFlg === 1 && (!barcodeScan || barcodeScan.applied === 0)">
              <div>
                <div class="hd1">
                  <h3>バーコードスキャン機能</h3>
                  <p class="com_fs14 com_mgb5">バーコードスキャン機能にお申し込みいただくと、スマートフォンアプリにてバーコードスキャンがご利用いただけるようになります。</p>
                  <p class="com_fs14 com_color3 com_mgb0">{{ barcodeApplyExplanation }}</p>
                </div>
              </div>
              <div>
                <button type="button" class="btn com_btn6 b cl1" data-bs-toggle="modal" data-bs-target="#barcodeScanApplyModal">申し込み</button>
              </div>
            </div>
            <div v-if="(user.orderFlg === 0 || user.returnRequestFlg === 0) && barcodeScan?.isAvailable && barcodeScanDeviceList.length > 0 && user.barcodeScanApplyPermissionFlg === 1">
              <hr class="com_sep1">
              <h3 class="com_title3">バーコードスキャン機能を<br class="dsp-s">使用中のデバイス</h3>
              <p class="com_fs16 com_center1 com_left1s com_mgb0 com_mgb5s">スキャン機能をご利用中のデバイスは以下の通りです。<br class="dsp-w">接続を解除したい場合は「削除する」をクリックしてください。</p>
              <p class="com_fs14 com_center1 com_left1s com_color3">{{ barcodeScanUseDeviceExplanation }}</p>
              <div>
                <div class="com_cnt3"  v-for="device in barcodeScanDeviceList" :key="device.uuid">
                  <div>
                    <p class="com_fs16">
                      <img src="/front/assets/common/img/icon_mobile1.png" alt="" class="icon1">
                      {{ device.model }}
                      <span v-if="isCurrentDevice(device.uuid)">
                        （ご自身の端末）
                      </span>
                    </p>
                  </div>
                  <div>
                    <p class="com_fs12">最終ご利用日時：<br class="dsp-s">{{ formatDateWithHourMin(device.scannedAt) }}</p>
                  </div>
                  <div>
                    <button type="button" class="btn com_btn6 b cl1" @click="openDeleteModal(device)">削除する</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

  <EnifClubApplyModal
    :splitSiteCode="user.splitSiteCode"
    @applyEnifClub="applyEnifClub"
  ></EnifClubApplyModal>

  <BarcodeScanApplyModal
    :splitSiteCode="user.splitSiteCode"
    @applyBarcodeScan="applyBarcodeScan"
  ></BarcodeScanApplyModal>

  <DeleteBarcodeScanDeviceConfirmModal
  :operatingDeviceData="selectedDevice"
  @deleteBarcodeScanDevice="confirmDeleteDevice"
  />

  <DeleteBarcodeScanDeviceCompleteModal />

  <ApiErrorModal
    :messages="errorMessages"
  ></ApiErrorModal>

</template>

<style scoped>
.text-pre-wrap {
  white-space: pre-wrap
}

#personal-setting,
#password-setting,
#service-apply {
  scroll-margin-top: 160px;
}

@media only screen and (max-width: 821px) {
  #personal-setting,
  #password-setting,
  #service-apply {
    scroll-margin-top: 33vw;
  }
}
</style>